/**
 * Slice a neighbor `.zwdoc` into a short head/tail document for in-editor
 * peeks (#264). Keeps real blocks so ProseMirror can render the same
 * formatting as the open scene.
 */

import type { Node as PMNode } from "prosemirror-model";
import { isJust, just, nothing, type Maybe } from "./maybe";
import {
  emptyZwDocHeader,
  isZwDocText,
  parseZwDoc,
  serializeZwDoc,
} from "./zwdoc/format";
import { zwdocSchema } from "./zwdoc/schema";
import { plainTextToZwDoc } from "./zwdoc/review";
import { proseForWordCount } from "./wordCount";

export const SCENE_PEEK_PARAGRAPH_COUNT = 3;

export type ScenePeekSlice = {
  /** Serialized `.zwdoc` of the sliced blocks. */
  text: string;
  /** Top-level block count (for fade distance). */
  blockCount: number;
};

function contentBlocks(doc: PMNode): PMNode[] {
  const blocks: PMNode[] = [];
  doc.forEach((node) => {
    if (node.isTextblock && node.textContent.trim() === "") return;
    blocks.push(node);
  });
  return blocks;
}

function serializeBlocks(blocks: readonly PMNode[]): Maybe<ScenePeekSlice> {
  if (blocks.length === 0) return nothing;
  try {
    const doc = zwdocSchema.node("doc", null, [...blocks]);
    doc.check();
    return just({
      text: serializeZwDoc({ header: emptyZwDocHeader(), doc }),
      blockCount: blocks.length,
    });
  } catch {
    return nothing;
  }
}

function legacyParagraphTexts(raw: string): string[] {
  return proseForWordCount(raw)
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

function sliceBlocks(
  blocks: readonly PMNode[],
  edge: "head" | "tail",
  count: number,
): PMNode[] {
  const limit = Math.max(0, count);
  if (limit === 0 || blocks.length === 0) return [];
  return edge === "head"
    ? blocks.slice(0, limit)
    : blocks.slice(Math.max(0, blocks.length - limit));
}

/** First `count` non-empty blocks as a mini `.zwdoc` (next-scene peek). */
export function scenePeekHead(
  raw: string,
  count: number = SCENE_PEEK_PARAGRAPH_COUNT,
): Maybe<ScenePeekSlice> {
  if (isZwDocText(raw)) {
    const parsed = parseZwDoc(raw);
    if (parsed.ok === false) return nothing;
    return serializeBlocks(
      sliceBlocks(contentBlocks(parsed.value.doc), "head", count),
    );
  }
  const texts = legacyParagraphTexts(raw).slice(0, Math.max(0, count));
  if (texts.length === 0) return nothing;
  return just({
    text: plainTextToZwDoc(texts.join("\n\n")),
    blockCount: texts.length,
  });
}

/** Last `count` non-empty blocks as a mini `.zwdoc` (previous-scene peek). */
export function scenePeekTail(
  raw: string,
  count: number = SCENE_PEEK_PARAGRAPH_COUNT,
): Maybe<ScenePeekSlice> {
  if (isZwDocText(raw)) {
    const parsed = parseZwDoc(raw);
    if (parsed.ok === false) return nothing;
    return serializeBlocks(
      sliceBlocks(contentBlocks(parsed.value.doc), "tail", count),
    );
  }
  const all = legacyParagraphTexts(raw);
  if (count <= 0 || all.length === 0) return nothing;
  const texts = all.slice(Math.max(0, all.length - count));
  return just({
    text: plainTextToZwDoc(texts.join("\n\n")),
    blockCount: texts.length,
  });
}

/**
 * Opacity distance from the open body for a block at `index` in a peek.
 * Prev peeks: index 0 is farthest. Next peeks: index 0 is nearest.
 */
export function peekParagraphDistance(
  index: number,
  length: number,
  edge: "prev" | "next",
): number {
  if (length <= 0) return 1;
  if (edge === "next") return index + 1;
  return length - index;
}

/** True when a peek slice is present (for callers that want a boolean). */
export function hasScenePeekSlice(slice: Maybe<ScenePeekSlice>): boolean {
  return isJust(slice);
}
